import React, { SyntheticEvent, useEffect, useState } from "react";
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Checkbox,
  MenuItem,
  TextField,
} from "@mui/material";
import { getCodeData } from "../utils/CommonUtils";
import { StyleSettings } from "../../styles/StyleSettings";

/**
 * 조회조건에 명칭과 코드를 같이 보여준다. (예: 생산품 [M])
 * 명칭만으로는 어떤 코드인지 구분이 안 되는 경우가 있어 코드를 덧붙인다.
 * 코드가 없는 항목은 명칭만 그린다.
 */
const optionLabel = (option: any) => {
  const nm = option?.name ?? "";
  return option?.code ? `${nm} [${option.code}]` : nm;
};

interface SnMultiComboBoxProps {
  defaultValue?: string[];
  width?: number;
  code?: string;
  sql?: string;
  js_combo?: any;
  emptyLine?: boolean;
  onValueChanged?: (value: string) => void;
  isReadOnly?: boolean;
  isRequired?: boolean;
}

const SnMultiComboBox = React.memo(
  ({
    defaultValue,
    width,
    code,
    sql,
    js_combo,
    emptyLine,
    onValueChanged,
    isReadOnly = false,
    isRequired,
  }: SnMultiComboBoxProps) => {
    const [options, setOptions] = useState<any[]>([]);
    const [values, setValue] = useState<string[]>(
      defaultValue ? defaultValue : []
    );
    const [inputValue, setInputValue] = useState<string>("");
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [borderWidth, setBorderWidth] = useState("1px");

    const fetchDefaultValue = async () => {
      try {
        setValue(defaultValue ? defaultValue : []);
      } catch (error: any) {}
    };

    const fetchIsRequired = async () => {
      try {
        if (isRequired) setBorderWidth("2px");
        else setBorderWidth("1px");
      } catch (error: any) {}
    };

    useEffect(() => {
      fetchDefaultValue();
    }, [defaultValue]);

    useEffect(() => {
      fetchIsRequired();
    }, [isRequired]);

    // 코드값 조회 : 서버가 미리 resolve 한 js_combo 우선, 없으면 런타임 getCodeData 폴백
    useEffect(() => {
      const fetchData = async () => {
        try {
          const dataSource = Array.isArray(js_combo)
            ? js_combo
            : typeof js_combo === "string"
              ? JSON.parse(js_combo)
              : [];
          const data =
            dataSource.length > 0
              ? dataSource
              : await getCodeData(code || "", sql || "", emptyLine);
          // 정렬하지 않는다. js_combo(SearchMapper) · getCodeData(SearchServiceImpl)
          // 둘 다 서버에서 no_seq_sort 순으로 내려주므로 여기서 다시 정렬하면
          // 공통코드에 지정된 표시순서가 사라진다.
          setOptions(Array.isArray(data) ? data : data ? [data] : []);
        } catch (error) {
          console.error(error);
        }
      };
      fetchData();
    }, [code, sql, emptyLine, js_combo]);

    // 입력값 변경 핸들러
    const handleInputChange = (e: SyntheticEvent, value: string) => {
      setInputValue(value);
    };

    // 값이 변경될 때 호출되는 핸들러
    const handleChange = (e: SyntheticEvent, value: any) => {
      const selectedCodes = value.map((option: any) => option.code);
      setValue(selectedCodes);

      // 선택 후에도 검색어를 유지
      if (inputValue) {
        setInputValue(inputValue);
      }
    };

    // 포커스 진입 핸들러
    const handleFocus = () => {
      setIsFocused(true);
    };

    // 포커스 해제 핸들러
    const handleBlur = () => {
      setIsFocused(false);
      setInputValue(""); // 포커스 해제 시 입력값 초기화

      if (onValueChanged) {
        onValueChanged?.(values.join(","));
      }
    };

    // 공백 상태에서 backspace 방지 핸들러
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Backspace" && !inputValue) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    return (
      <div>
        <Autocomplete
          // disablePortal
          multiple
          fullWidth={true}
          sx={{
            "& .MuiInputBase-root": {
              height: StyleSettings.control_height,
              display: "flex",
              alignItems: "center",
              position: "relative",
            },
            "& .custom-adornment": {
              position: "absolute",
              left: "10px", // 텍스트 시작 위치
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "calc(100% - 60px)", // 입력 영역 침범 방지
              pointerEvents: "none", // 포커스 방해 방지
            },
            backgroundColor: "white",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderWidth: borderWidth, // 테두리 두께 설정
              },
              "&:hover fieldset": {
                borderWidth: borderWidth, // 호버 시 두께 유지
              },
              "&.Mui-focused fieldset": {
                borderWidth: borderWidth, // 포커스 시 두께 유지
              },
            },
          }}
          options={options}
          value={options.filter((option: any) => values.includes(option.code))}
          inputValue={inputValue}
          onChange={handleChange}
          onInputChange={handleInputChange}
          onBlur={handleBlur}
          clearOnBlur={false} // 포커스 해제 시 검색어 유지
          getOptionLabel={(option: any) => optionLabel(option)}
          filterOptions={(options, { inputValue }) =>
            inputValue
              ? options.filter((option) =>
                  // 명칭·코드 어느 쪽을 쳐도 걸리게 한다.
                  optionLabel(option)
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                )
              : options
          }
          disableCloseOnSelect
          isOptionEqualToValue={(option, value) => option.code === value?.code}
          // 드롭다운 한 줄을 SnComboBox 와 같게 맞춘다.
          // 맨 li 로 두면 MUI 기본 body1(16px)을 받아 단일 콤보보다 글자가 커 보인다.
          // 체크박스는 기본 padding 9px 이 붙어 행이 두꺼워지므로 작게 줄여 32px 안에 넣는다.
          renderOption={(props, option, { selected }) => (
            <MenuItem
              {...props}
              key={option.code}
              sx={{ fontSize: StyleSettings.font, minHeight: "32px" }}
            >
              <Checkbox
                checked={selected}
                size="small"
                sx={{ p: 0, mr: 1 }}
              />
              {optionLabel(option)}
            </MenuItem>
          )}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField
              {...params}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              slotProps={{
                input: {
                  ...params.InputProps,
                  style: {
                    height: StyleSettings.control_height,
                    paddingTop: 1,
                    paddingBottom: 1,
                    paddingRight: 20,
                    boxSizing: "border-box",
                    fontSize: StyleSettings.font,
                  },
                  startAdornment:
                    !isFocused && values.length > 0 ? (
                      <span
                        className="custom-adornment"
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "calc(100% - 70px)",
                        }}
                      >
                        {values
                          .map((value) =>
                            optionLabel(
                              options.find((o: any) => o.code === value)
                            )
                          )
                          .join(", ")}
                      </span>
                    ) : null,
                },
              }}
            />
          )}
          slotProps={{
            listbox: {
              style: {
                maxHeight: 250,
                overflow: "auto",
              },
            },
          }}
          disabled={isReadOnly}
        />
      </div>
    );
  }
);

export default SnMultiComboBox;
